import { useEffect, useState } from "react";
import { ChevronDown, Loader2 } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { setSiteLanguage, setTranslatorBusyListener } from "@/lib/translator";
import { getCurrency, getRate, setCurrency } from "@/lib/currency";

const LANGS = [
  { code: "it", flag: "it", label: "Italiano" },
  { code: "en", flag: "gb", label: "English" },
  { code: "de", flag: "de", label: "Deutsch" },
  { code: "fr", flag: "fr", label: "Français" },
  { code: "es", flag: "es", label: "Español" },
  { code: "ja", flag: "jp", label: "日本語" },
];
const CURRENCIES = [{ code: "EUR", sign: "€" }, { code: "USD", sign: "$" }];

const Flag = ({ code }) => <img src={`/flags/${code}.svg`} alt="" className="h-3.5 w-5 rounded-[2px] object-cover shadow-sm ring-1 ring-black/10" />;

export const LanguageSwitcher = () => {
  const [lang, setLang] = useState(() => localStorage.getItem("cs_lang") || "it");
  const [currency, setCur] = useState(getCurrency);
  const [busy, setBusy] = useState(false);
  const [rate, setRate] = useState(null);
  useEffect(() => {
    setTranslatorBusyListener(setBusy);
  }, []);
  useEffect(() => {
    localStorage.setItem("cs_lang", lang);
    setSiteLanguage(lang);
  }, [lang]);
  useEffect(() => {
    setCurrency(currency).then(() => setRate(getRate()));
  }, [currency]);
  const cur = LANGS.find((l) => l.code === lang) || LANGS[0];
  return (
    <div data-no-translate data-no-currency>
      <DropdownMenu modal={false}>
        <DropdownMenuTrigger className="flex items-center gap-1.5 rounded-full border border-black/10 px-2.5 py-1.5 text-xs font-bold uppercase hover:border-amber-500/40" data-testid="lang-switcher">
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" data-testid="lang-loading" /> : <Flag code={cur.flag} />}
          <span data-testid="lang-current">{cur.code.toUpperCase()}</span>
          {currency === "USD" && <span className="hidden text-cyan-700 sm:inline" data-testid="currency-current">· $</span>}
          <ChevronDown className="h-3 w-3 opacity-60" />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56 border-black/10 bg-white" data-no-translate data-no-currency data-testid="lang-menu">
          <DropdownMenuLabel className="text-[10px] uppercase tracking-widest text-stone-500">Lingua · Language</DropdownMenuLabel>
          {LANGS.map((l) => (
            <DropdownMenuItem key={l.code} onClick={() => setLang(l.code)} className={`gap-2 ${l.code === lang ? "font-bold" : ""}`} data-testid={`lang-option-${l.code}`}>
              <Flag code={l.flag} /><span className="w-6 text-xs font-bold">{l.code.toUpperCase()}</span>{l.label}
            </DropdownMenuItem>
          ))}
          <DropdownMenuSeparator />
          <DropdownMenuLabel className="text-[10px] uppercase tracking-widest text-stone-500">Valuta · Currency</DropdownMenuLabel>
          {CURRENCIES.map((c) => (
            <DropdownMenuItem key={c.code} onClick={() => setCur(c.code)} className={`gap-2 ${c.code === currency ? "font-bold" : ""}`} data-testid={`currency-option-${c.code.toLowerCase()}`}>
              <span className="w-5 text-center font-mono">{c.sign}</span><span className="w-8 text-xs font-bold">{c.code}</span>{c.code === "EUR" ? "€" : "€ + $"}
            </DropdownMenuItem>
          ))}
          {currency === "USD" && rate && (
            <p className="px-2 pb-1.5 pt-1 text-[11px] leading-snug text-stone-500" data-testid="currency-rate">
              1 € = {rate.rate.toFixed(4)} $ · BCE / ECB {rate.rate_date}. Payment in EUR.
            </p>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};
